<template>
  <header class="app-navbar">
    <!-- Left: Toggle + Breadcrumb -->
    <div class="navbar-left">
      <button class="sidebar-toggle" @click="$emit('toggle-sidebar')">
        <i :class="sidebarCollapsed ? 'fas fa-indent' : 'fas fa-outdent'"></i>
      </button>

      <div v-if="isImpersonating" class="impersonation-badge">
        <i class="fas fa-exclamation-triangle"></i>
        <span>Sesión como otro usuario</span>
        <button class="impersonation-stop-btn" @click="stopImpersonating">Volver</button>
      </div>

      <AppBreadcrumbs :currentPage="currentRouteName" />
    </div>

    <!-- Right: User -->
    <div class="navbar-right">
      <div class="user-menu" ref="userMenuRef">
        <button class="user-btn" @click="showMenu = !showMenu">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-info">
            <span class="user-name">{{ user.username }}</span>
            <span class="user-role">Administrador</span>
          </div>
          <i class="fas fa-chevron-down user-chevron"></i>
        </button>

        <transition name="dropdown">
          <div v-if="showMenu" class="user-dropdown">
            <button class="dropdown-item-btn" @click="logout">
              <i class="fas fa-sign-out-alt"></i>
              Cerrar sesión
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import AppBreadcrumbs from '../Breadcrumbs.vue'
import ROUTES from '../../constansRoute.js'
import { useImpersonate } from '../../composables/useImpersonate.js'

export default {
  name: 'AppNavbar',
  components: { AppBreadcrumbs },
  props: {
    sidebarCollapsed: { type: Boolean, default: false },
  },
  emits: ['toggle-sidebar'],
  setup() {
    const showMenu = ref(false)
    const userMenuRef = ref(null)
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const currentRouteName = computed(() => route.name)
    const user = store.getters['appUserdata/getUser']
    const userInitials = computed(() => {
      if (!user?.username) return '?'
      return user.username.substring(0, 2).toUpperCase()
    })

    const { isImpersonating, stopImpersonating } = useImpersonate()

    const logout = () => {
      showMenu.value = false
      store.dispatch('appAuth/logout').then(() => {
        router.push({ name: ROUTES.SIGNIN })
      })
    }

    const handleClickOutside = (e) => {
      if (userMenuRef.value && !userMenuRef.value.contains(e.target)) {
        showMenu.value = false
      }
    }

    onMounted(() => document.addEventListener('click', handleClickOutside))
    onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))

    return { showMenu, userMenuRef, currentRouteName, user, userInitials, logout, isImpersonating, stopImpersonating }
  },
}
</script>

<style scoped>
.app-navbar {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 20px;
  background: #ffffff;
  border-bottom: 1px solid #E2E8F0;
  flex-shrink: 0;
  z-index: 30;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.navbar-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

/* Toggle button */
.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #64748B;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sidebar-toggle:hover {
  background: #F1F5F9;
  color: #334155;
}

/* User menu */
.user-menu {
  position: relative;
}

.user-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.user-btn:hover {
  background: #F8FAFC;
}

.user-avatar {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.user-info {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.user-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  line-height: 1.2;
}

.user-role {
  font-size: 0.65rem;
  color: #94A3B8;
  line-height: 1.2;
}

.user-chevron {
  font-size: 0.6rem;
  color: #94A3B8;
}

/* Dropdown */
.user-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  width: 176px;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 4px;
  z-index: 50;
}

.dropdown-item-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 0.82rem;
  color: #EF4444;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
}

.dropdown-item-btn:hover {
  background: #FEF2F2;
}

/* Impersonation badge */
.impersonation-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  background: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 8px;
  color: #92400E;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.impersonation-stop-btn {
  padding: 2px 10px;
  border: 1px solid #F59E0B;
  background: #F59E0B;
  color: #fff;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.impersonation-stop-btn:hover {
  background: #D97706;
}

/* Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}
</style>
